import {useRef} from 'react';

import {useScroll, useSpring, useTransform} from 'framer-motion';

import {useVideoPlay} from 'UI/hooks/useVideoPlay';

/**
 * The `useFullsizeVideo` hook orchestrates scroll-driven animations and video playback controls for a full-size video section.
 * It tracks vertical scroll progress independently for the video and text elements, applying a spring-based scale animation to the video and a parallax horizontal translation to the text.
 * The hook also provides play/pause controls for the video element and retrieves the current theme color palette for consistent styling.
 *
 * @returns An object containing `colors` with the theme palette, `pauseVideo` and `playVideo` functions for controlling playback, `scale` as a MotionValue for the video zoom effect, `scrollRef` and `textRef` as refs for scroll tracking targets, `translateX` as a MotionValue for horizontal text parallax, and `videoRef` as a ref to the video element.
 *
 * @example
 * ```tsx
 * const {colors, pauseVideo, playVideo, scale, scrollRef, textRef, translateX, videoRef} = useFullsizeVideo();
 * ```
 */
export const useFullsizeVideo = () => {
    const scrollRef = useRef(null);
    const videoRef = useRef<HTMLVideoElement>(null);

    const {scrollYProgress: scrollYProgressVideo} = useScroll({
        offset: ['start end', '0.8 start'],
        target: scrollRef
    });

    const videoScale = useSpring(scrollYProgressVideo, {
        damping: 20,
        restDelta: 0.001,
        stiffness: 100
    });

    const {pauseVideo, playVideo} = useVideoPlay(videoRef);

    const scale = useTransform(videoScale, [0, 0.4], [0.3, 1]);

    return {
        pauseVideo,
        playVideo,
        scale,
        scrollRef,
        videoRef
    };
};